/**
 * Property Form Validation Utilities
 * Provides validation functions for property data
 */

import { VALIDATION_RULES } from '../constants/propertyConstants';

/**
 * Validate property name
 */
export const validatePropertyName = (name) => {
    const errors = [];

    if (!name || !name.trim()) {
        errors.push('Property name is required');
        return errors;
    }

    if (name.length < VALIDATION_RULES.PROPERTY_NAME.minLength) {
        errors.push(`Property name must be at least ${VALIDATION_RULES.PROPERTY_NAME.minLength} characters`);
    }

    if (name.length > VALIDATION_RULES.PROPERTY_NAME.maxLength) {
        errors.push(`Property name must be less than ${VALIDATION_RULES.PROPERTY_NAME.maxLength} characters`);
    }

    return errors;
};

/**
 * Validate address
 */
export const validateAddress = (address) => {
    const errors = [];

    if (!address || !address.trim()) {
        errors.push('Address is required');
        return errors;
    }

    if (address.length < VALIDATION_RULES.ADDRESS.minLength) {
        errors.push(`Address must be at least ${VALIDATION_RULES.ADDRESS.minLength} characters`);
    }

    if (address.length > VALIDATION_RULES.ADDRESS.maxLength) {
        errors.push(`Address must be less than ${VALIDATION_RULES.ADDRESS.maxLength} characters`);
    }

    return errors;
};

/**
 * Validate city
 */
export const validateCity = (city) => {
    const errors = [];

    if (!city || !city.trim()) {
        errors.push('City is required');
        return errors;
    }

    if (city.length < VALIDATION_RULES.CITY.minLength) {
        errors.push(`City must be at least ${VALIDATION_RULES.CITY.minLength} characters`);
    }

    if (city.length > VALIDATION_RULES.CITY.maxLength) {
        errors.push(`City must be less than ${VALIDATION_RULES.CITY.maxLength} characters`);
    }

    return errors;
};

/**
 * Validate price
 */
export const validatePrice = (price, fieldName = 'Price') => {
    const errors = [];

    if (price === null || price === undefined || price === '') {
        errors.push(`${fieldName} is required`);
        return errors;
    }

    const numPrice = Number(price);

    if (isNaN(numPrice)) {
        errors.push(`${fieldName} must be a valid number`);
        return errors;
    }

    if (numPrice <= 0) {
        errors.push(`${fieldName} must be greater than 0`);
    }

    return errors;
};

/**
 * Validate description
 */
export const validateDescription = (description) => {
    const errors = [];

    if (!description || !description.trim()) {
        // Description is optional but if provided must meet requirements
        return errors;
    }

    if (description.length < VALIDATION_RULES.DESCRIPTION.minLength) {
        errors.push(`Description must be at least ${VALIDATION_RULES.DESCRIPTION.minLength} characters`);
    }

    if (description.length > VALIDATION_RULES.DESCRIPTION.maxLength) {
        errors.push(`Description must be less than ${VALIDATION_RULES.DESCRIPTION.maxLength} characters`);
    }

    return errors;
};

/**
 * Validate property type
 */
export const validatePropertyType = (propertyTypeId) => {
    const errors = [];

    if (!propertyTypeId) {
        errors.push('Property type is required');
    }

    return errors;
};

/**
 * Validate number in range
 */
export const validateNumberInRange = (value, fieldName, min, max) => {
    const errors = [];

    if (value === null || value === undefined || value === '') {
        // Optional field
        return errors;
    }

    const numValue = Number(value);

    if (isNaN(numValue)) {
        errors.push(`${fieldName} must be a valid number`);
        return errors;
    }

    if (min !== null && numValue < min) {
        errors.push(`${fieldName} must be at least ${min}`);
    }

    if (max !== null && numValue > max) {
        errors.push(`${fieldName} must be less than ${max}`);
    }

    return errors;
};

/**
 * Validate basic information step
 */
export const validateBasicInformation = (data) => {
    const errors = {};

    const nameErrors = validatePropertyName(data.propertyName);
    if (nameErrors.length > 0) {
        errors.propertyName = nameErrors[0];
    }

    const typeErrors = validatePropertyType(data.propertyTypeId);
    if (typeErrors.length > 0) {
        errors.propertyTypeId = typeErrors[0];
    }

    const addressErrors = validateAddress(data.addressLine1);
    if (addressErrors.length > 0) {
        errors.addressLine1 = addressErrors[0];
    }

    const cityErrors = validateCity(data.city);
    if (cityErrors.length > 0) {
        errors.city = cityErrors[0];
    }

    return errors;
};

/**
 * Validate property details step
 */
export const validatePropertyDetails = (data) => {
    const errors = {};

    const bedroomErrors = validateNumberInRange(
        data.bedrooms,
        'Bedrooms',
        VALIDATION_RULES.BEDROOMS.min,
        VALIDATION_RULES.BEDROOMS.max
    );
    if (bedroomErrors.length > 0) {
        errors.bedrooms = bedroomErrors[0];
    }

    const bathroomErrors = validateNumberInRange(
        data.bathrooms,
        'Bathrooms',
        VALIDATION_RULES.BATHROOMS.min,
        VALIDATION_RULES.BATHROOMS.max
    );
    if (bathroomErrors.length > 0) {
        errors.bathrooms = bathroomErrors[0];
    }

    const sqftErrors = validateNumberInRange(
        data.squareFeet,
        'Square Feet',
        VALIDATION_RULES.SQUARE_FEET.min,
        VALIDATION_RULES.SQUARE_FEET.max
    );
    if (sqftErrors.length > 0) {
        errors.squareFeet = sqftErrors[0];
    }

    const descErrors = validateDescription(data.propertyDescription);
    if (descErrors.length > 0) {
        errors.propertyDescription = descErrors[0];
    }

    return errors;
};

/**
 * Validate pricing and terms step
 */
export const validatePricingAndTerms = (data) => {
    const errors = {};

    const priceErrors = validatePrice(data.pricePerMonth, 'Monthly rent');
    if (priceErrors.length > 0) {
        errors.pricePerMonth = priceErrors[0];
    }

    // Security deposit is optional
    if (data.securityDeposit) {
        const depositErrors = validatePrice(data.securityDeposit, 'Security deposit');
        if (depositErrors.length > 0) {
            errors.securityDeposit = depositErrors[0];
        }
    }

    if (!data.currency) {
        errors.currency = 'Currency is required';
    }

    if (!data.leaseType) {
        errors.leaseType = 'Lease type is required';
    }

    if (!data.minimumLeaseTerm || data.minimumLeaseTerm < 1) {
        errors.minimumLeaseTerm = 'Minimum lease term must be at least 1 month';
    }

    if (!data.furnishingStatus) {
        errors.furnishingStatus = 'Furnishing status is required';
    }

    return errors;
};

/**
 * Validate complete property data
 */
export const validatePropertyData = (data) => {
    const errors = {
        ...validateBasicInformation(data),
        ...validatePropertyDetails(data),
        ...validatePricingAndTerms(data)
    };

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

/**
 * Check if step is valid
 */
export const isStepValid = (stepNumber, data) => {
    let errors = {};

    switch (stepNumber) {
        case 1:
            errors = validateBasicInformation(data);
            break;
        case 2:
            errors = validatePropertyDetails(data);
            break;
        case 3:
            errors = validatePricingAndTerms(data);
            break;
        case 4:
        case 5:
        case 6:
            // These steps have optional fields
            return true;
        case 7:
            // Review step - validate everything
            const validation = validatePropertyData(data);
            return validation.isValid;
        default:
            return true;
    }

    return Object.keys(errors).length === 0;
};

export default {
    validatePropertyName,
    validateAddress,
    validateCity,
    validatePrice,
    validateDescription,
    validatePropertyType,
    validateNumberInRange,
    validateBasicInformation,
    validatePropertyDetails,
    validatePricingAndTerms,
    validatePropertyData,
    isStepValid
};
